import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Paperclip, Send, X } from 'lucide-react';
import type { Api } from '../adapters/Api';
import type { SupportMessage } from '../domain/types';
import { useI18n } from '../i18n';
import { formatRelative } from './status';

// Chat de soporte (doc 14): burbuja flotante abajo a la derecha — patrón
// Intercom/Crisp — disponible para cualquier usuario logueado (también en la
// pantalla de Suscripción: los problemas de pago son los más urgentes). El
// usuario escribe y adjunta capturas; las respuestas del dueño (vía Telegram)
// aparecen aquí, con badge de no-leídos cuando el chat está cerrado.

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // debe coincidir con el bucket (migración 004)

type Translate = ReturnType<typeof useI18n>['t'];

export function SupportWidget({ api }: { api: Api }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [formError, setFormError] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Cerrado se sondea lento (solo alimenta el badge); abierto, como un chat.
  const query = useQuery({
    queryKey: ['supportMessages'],
    queryFn: () => api.listSupportMessages(),
    refetchInterval: open ? 5000 : 30000
  });
  const messages = query.data ?? [];
  const unread = messages.filter((m) => m.sender === 'support' && !m.readAt).length;

  const send = useMutation({
    mutationFn: () => api.sendSupportMessage(draft.trim(), attachment),
    onSuccess: () => {
      setDraft('');
      setAttachment(null);
      setFormError('');
      queryClient.invalidateQueries({ queryKey: ['supportMessages'] });
    },
    onError: () => setFormError(t('support.sendError'))
  });

  const markRead = useMutation({
    mutationFn: () => api.markSupportRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['supportMessages'] })
  });

  // Con el chat abierto, todo lo no-leído queda leído (también si llega en vivo).
  useEffect(() => {
    if (open && unread > 0 && !markRead.isPending) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, unread]);

  // Pega la vista al último mensaje (al abrir y cuando llegan nuevos).
  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages.length]);

  function onPickFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = ''; // permite volver a elegir el mismo archivo
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormError(t('support.attachNotImage'));
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setFormError(t('support.attachTooBig'));
      return;
    }
    setFormError('');
    setAttachment(file);
  }

  const canSend = !send.isPending && (draft.trim().length > 0 || !!attachment);

  function submit() {
    if (!canSend) return;
    send.mutate();
  }

  return (
    <div className="support-widget">
      {open && (
        <div className="support-panel" role="dialog" aria-label={t('support.title')}>
          <div className="support-head">
            <div className="support-head-titles">
              <strong>{t('support.title')}</strong>
              <span>{t('support.subtitle')}</span>
            </div>
            <button
              type="button"
              className="icon-button"
              aria-label={t('support.close')}
              onClick={() => setOpen(false)}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>

          <div className="support-messages" ref={listRef}>
            {query.isLoading && <div className="support-hint">{t('support.loading')}</div>}
            {!query.isLoading && messages.length === 0 && (
              <div className="support-hint">{t('support.empty')}</div>
            )}
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} t={t} />
            ))}
            {send.isPending && <div className="support-hint">{t('support.sending')}</div>}
          </div>

          {formError && <div className="notice warning support-error">{formError}</div>}

          <form
            className="support-composer"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            {attachment && (
              <div className="support-attachment-chip">
                <span>{attachment.name}</span>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('support.attachRemove')}
                  onClick={() => setAttachment(null)}
                >
                  <X size={13} aria-hidden="true" />
                </button>
              </div>
            )}
            <div className="support-input-row">
              <button
                type="button"
                className="icon-button"
                aria-label={t('support.attach')}
                title={t('support.attach')}
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip size={17} aria-hidden="true" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onPickFile} />
              <textarea
                rows={2}
                value={draft}
                placeholder={t('support.placeholder')}
                maxLength={4000}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    submit();
                  }
                }}
              />
              <button
                type="submit"
                className="primary support-send"
                disabled={!canSend}
                aria-label={t('support.send')}
                title={t('support.send')}
              >
                <Send size={16} aria-hidden="true" />
              </button>
            </div>
          </form>
        </div>
      )}

      <button
        type="button"
        className="support-launcher"
        aria-label={open ? t('support.close') : t('support.open')}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <MessageCircle size={22} aria-hidden="true" />
        {!open && unread > 0 && (
          <span className="support-badge" aria-label={t('support.unreadLabel', { count: String(unread) })}>
            {unread}
          </span>
        )}
      </button>
    </div>
  );
}

function MessageBubble({ message, t }: { message: SupportMessage; t: Translate }) {
  const fromUser = message.sender === 'user';
  return (
    <div className={`support-bubble ${fromUser ? 'from-user' : 'from-support'}`}>
      <div className="support-bubble-meta">
        <strong>{fromUser ? t('support.you') : t('support.agent')}</strong>
        <span>{formatRelative(message.createdAt, t)}</span>
      </div>
      {message.attachmentUrl && (
        <a href={message.attachmentUrl} target="_blank" rel="noopener">
          <img src={message.attachmentUrl} alt={t('support.imageAlt')} />
        </a>
      )}
      {message.body && <p>{message.body}</p>}
    </div>
  );
}
